import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EditorState } from '@models/editorState/editorState.model';
import { EditorTab } from '@models/editorState/editorTab.model';
import { EditorStateService } from '@services/editorState/editor-state.service';
import { ConfirmDialogComponent } from '@views/escrito-editor/dialogs/confirm-dialog/confirm-dialog/confirm-dialog.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-editor-tabs',
  standalone: true,
  imports: [CommonModule,MatIconModule,MatButtonModule,NgxSpinnerModule,MatFormFieldModule,ReactiveFormsModule,MatTooltipModule],
  templateUrl: './editor-tabs.component.html',
  styleUrl: './editor-tabs.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditorTabsComponent {

  editorState!: EditorState;
  private destroy$ = new Subject<void>();

  constructor(
    private editorStateService: EditorStateService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.editorStateService.getEditorState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.editorState = state;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTabClick(tab: EditorTab): void {
    this.editorStateService.setActiveTab(tab.id);
  }

  onTabClose(event: Event, tab: EditorTab): void {
    event.stopPropagation();

    if (tab.hasChanges) {
      this.showCloseConfirmation(tab);
    } else {
      this.editorStateService.closeTab(tab.id, true);
    }
  }

  private showCloseConfirmation(tab: EditorTab): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Cerrar Tab',
        message: `El ${tab.tipo} "${tab.titulo}" tiene cambios sin guardar. ¿Deseas cerrar sin guardar?`,
        confirmText: 'Cerrar sin Guardar',
        cancelText: 'Cancelar',
        showSaveButton: true,
        saveText: 'Guardar y Cerrar'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        // Cerrar sin guardar
        this.editorStateService.closeTab(tab.id, true);
      } else if (result === 'save') {
        // Guardar y cerrar
        this.saveAndCloseTab(tab);
      }
      // Si es 'cancel' o falsy, no hacer nada
    });
  }

  private saveAndCloseTab(tab: EditorTab): void {
    this.editorStateService.setActiveTab(tab.id);

    this.editorStateService.saveActiveTab().subscribe({
      next: () => {
        this.editorStateService.closeTab(tab.id, true);
      },
      error: (error) => {
        console.error('Error guardando tab:', error);
        // Mostrar error al usuario
      }
    });
  }

  onNewTab(): void {
    this.editorStateService.openTab({
      tipo: 'escrito',
      titulo: 'Nuevo Escrito',
      contenido: ''
    });
  }

  getTabIcon(tab: EditorTab): string {
    return tab.tipo === 'escrito' ? 'edit_note' : 'description';
  }

  getTabTooltip(tab: EditorTab): string {
    const tipo = tab.tipo === 'escrito' ? 'Escrito' : 'Plantilla';
    const estado = tab.hasChanges ? ' (modificado)' : '';
    const lastSaved = tab.lastSaved ? ` - Último guardado: ${this.formatDate(tab.lastSaved)}` : '';

    return `${tipo}: ${tab.titulo}${estado}${lastSaved}`;
  }

  private formatDate(date: Date): string {
    return new Intl.DateTimeFormat('es-AR', {
      timeStyle: 'short',
      dateStyle: 'short'
    }).format(date);
  }

  // Para el drag & drop de tabs (opcional)
  onTabDragStart(event: DragEvent, tab: EditorTab): void {
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', tab.id);
    }
  }

  onTabDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onTabDrop(event: DragEvent, targetTab: EditorTab): void {
    event.preventDefault();
    const draggedTabId = event.dataTransfer?.getData('text/plain');

    if (draggedTabId && draggedTabId !== targetTab.id) {
      // Implementar reordenamiento de tabs
      console.log('Reordenar tabs:', draggedTabId, 'antes de', targetTab.id);
    }
  }
}

